import { BevelCutTypePreference, TrussEnvelope } from '@paragon/contract-library';
import { Direction2D, Line2D, LineSegment2D, Point2D } from '@paragon/geometry-library';

import { findNextName } from '../../../utilities/findNextName';
import { GeometricEcosystem } from '../../ecosystem/GeometricEcosystem';
import { StageOneTrussEnvelope } from '../calculation/stage1';

export class TrussEnvelopeRunCalculator {
  readonly baseEnvelope: TrussEnvelope;

  readonly baseSegment: LineSegment2D;
  readonly spacing: number;

  readonly stoppingPoint: Point2D;

  runDirection!: Direction2D;

  constructor(baseEnvelope: TrussEnvelope, spacing: number, stoppingPoint: Point2D) {
    this.baseEnvelope = baseEnvelope;
    this.baseSegment = LineSegment2D.fromPointObjects(baseEnvelope.leftPoint, baseEnvelope.rightPoint);
    this.spacing = spacing;

    this.stoppingPoint = stoppingPoint;

    this.determineRunDirection();
  }

  determineRunDirection() {
    this.runDirection = this.baseSegment.direction.rotateQuarterTurn();

    if (
      this.stoppingPoint.distanceToLine(this.createParallelLine(this.spacing)) >
      this.stoppingPoint.distanceToLine(this.createParallelLine(this.spacing, this.runDirection.reverse()))
    ) {
      this.runDirection = this.runDirection.reverse();
    }
  }

  *calculateRunLines() {
    for (let distance = this.spacing; true; distance += this.spacing) {
      const line = this.createParallelLine(distance);

      if (this.stoppingPoint.directionFromLine(line).equals(this.runDirection)) {
        yield line;
      } else {
        break;
      }
    }
  }

  *calculateRunEnvelopes(ecosystem: GeometricEcosystem, namesInLayout: readonly string[]) {
    let takenNames: readonly string[] = namesInLayout;

    for (const line of this.calculateRunLines()) {
      // FIXME: using mouse position only leaves something to be desired in many cases. It would be nice to "detect the
      //   cavity" into which the user desires to place the run, and then filter all envelopes to be within that cavity,
      //   or tweak the defining point on a per-envelope basis to be always within that cavity.
      //
      // That would be a lot of work.
      const definingPoint = this.stoppingPoint.projectOntoLine(line);

      const name = findNextName(takenNames);
      takenNames = takenNames.concat(name);

      yield new StageOneTrussEnvelope(definingPoint, line, {
        ecosystem,
      })
        ?.boundIntoSegmentAroundDefiningPoint(this.baseEnvelope)
        ?.adjustToAccountForBevels(inferBevelPreferenceFromEnvelope(this.baseEnvelope))
        ?.getResult()
        ?.toTrussEnvelope(name, {
          ...this.baseEnvelope,
          guid: crypto.randomUUID(),
          componentDesignGuid: null,
        });
    }
  }

  *createRunEnvelopesWithoutCalculation(namesInLayout: readonly string[]): Generator<TrussEnvelope> {
    let takenNames: readonly string[] = namesInLayout;

    for (const line of this.calculateRunLines()) {
      const name = findNextName(takenNames);
      takenNames = takenNames.concat(name);

      yield {
        ...this.baseEnvelope,

        name,
        guid: crypto.randomUUID(),

        leftPoint: Point2D.fromObject(this.baseEnvelope.leftPoint).projectOntoLine(line).toObject(),
        rightPoint: Point2D.fromObject(this.baseEnvelope.rightPoint).projectOntoLine(line).toObject(),

        componentDesignGuid: null,
      };
    }
  }

  private createParallelLine(distance: number, direction: Direction2D = this.runDirection) {
    return new Line2D(this.baseSegment.basePoint.translate(direction.toVector(distance)), this.baseSegment.direction);
  }
}

/** A quick and dirty way to infer what types of bevel cuts should be generated on the run envelopes.
 * Tries to restrict bevel cuts to the types present on the base envelope. */
const inferBevelPreferenceFromEnvelope = (envelope: TrussEnvelope): BevelCutTypePreference =>
  !envelope.leftBevelCut && !envelope.rightBevelCut
    ? 'PreferNone'
    : [envelope.leftBevelCut, envelope.rightBevelCut].every((cut) => cut?.type !== 'Back' && cut?.type !== 'Front')
      ? 'PreferDouble'
      : 'PreferAny';
